import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { LeadStatus } from '@/services/leadService';

type Variant = 'default' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';

/**
 * Lead-status → badge-variant mapping. Constrained to the Resend-style
 * monochrome-with-tinted-status palette (docs/07 §3.2 — no ``--color-info``):
 *
 *   new       → default (darkest; draws the eye for unhandled leads)
 *   engaged   → secondary (subtle; the conversation is in flight)
 *   qualified → warning (needs follow-up; owner action expected)
 *   follow_up → outline (waiting state; not yet won or lost)
 *   won       → success (closed-positive)
 *   lost      → destructive (closed-negative; red tint)
 */
const STATUS_VARIANT: Record<LeadStatus, Variant> = {
  new: 'default',
  engaged: 'secondary',
  qualified: 'warning',
  follow_up: 'outline',
  won: 'success',
  lost: 'destructive',
};

interface LeadStatusBadgeProps {
  status: LeadStatus;
  className?: string;
  'data-testid'?: string;
}

export function LeadStatusBadge({
  status,
  className,
  'data-testid': testId,
}: LeadStatusBadgeProps) {
  const { t } = useTranslation();
  const variant = STATUS_VARIANT[status];
  return (
    <Badge
      variant={variant}
      className={className}
      data-testid={testId}
      data-variant={variant}
      data-status={status}
    >
      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {t(`leads.status.${status}`)}
    </Badge>
  );
}
